import React from "react";
import styled from "styled-components";
import { useState, useRef } from "react";
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import classNames from "classnames";
import { AutomationPlacement, AutomationTestIdElementType, createDataTestId } from '../../../utilities/automation';
import NavButton, {NavButtonVariants} from "../NavButton";
import useLanguage from "../../../utilities/useLanguage";
import { getTranslation } from "../translations";
import getTargetLink from "../../../utilities/getTargetLink";

function convertDesktopMenuToMobileMenu(desktopMenu) {
    // extract links from four column array format that desktop uses for display
    return desktopMenu.map(item => {
        let links;
        if (item.links) {
            links = item.links.reduce((acc, curr) => {
                acc.push(...curr);
                return acc;
            }, []);
        }
        return {
            ...item,
            links,
        };
    });
}
export default ({ menu: items = [], utilityNav = [], height }) => {
    const [open, setOpen] = useState(false);
    const [openMenuStack, setOpenMenuStack] = useState([]);
    const [activeMenu, setActiveMenu] = useState(null);
    const { lang } = useLanguage();
    const translations = getTranslation(lang);

    function toggleMenu() {
        setOpenMenuStack([]);
        setOpen(!open);
        document.body.classList.toggle('no-scroll', !open);
        window.scrollTo(0, 0);
    }

    function toggleSubMenu(level, menuName) {
        if (menuName) {
            openMenuStack[level] = menuName;
            setActiveMenu(menuName);
        } else {
            openMenuStack.pop();
            setActiveMenu(openMenuStack.at(-1) || null);
        }

        setOpenMenuStack([...openMenuStack]);
    }

    const menu = {
        openMenuStack,
        toggleMenu,
        toggleSubMenu,
        activeMenu
    };

    return (
        <>
            <Hamburger open={open} toggleOpen={toggleMenu} />
            <Sidebar items={convertDesktopMenuToMobileMenu(items)} menu={menu} open={open} utilityNav={utilityNav} translations={translations}/>
        </>
    );
};

const Hamburger = styled(({ open, className = '', toggleOpen }) => {
    return (
        <button className={classNames("menu-icon-container d-lg-none", className)}
                onClick={toggleOpen}
                type="button"
                data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.BUTTON, specifier: 'toggleHamburger'})}
                aria-label="Show Main Menu"
                aria-expanded={open}>
            <div className={classNames("menu-icon", { "open": open })}>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
            </div>
        </button>
    );
})`
display: block;
left: 18px;
top: 50%;
width: 50px;
height: 100%;
padding: 36px 6px 36px 24px;
background: none;
color: #000;
border: none;
margin-right: 1rem;

.menu-icon {
    & {
        display: block;
        width: 100%;
        height: 100%;
        position: relative;
        transform: rotate(0deg);
        transition: .5s ease-in-out;
        cursor: pointer;
    }
    span {
        display: block;
        position: absolute;
        height: 2px;
        width: 100%;
        background: #000;
        border-radius: 2px;
        opacity: 1;
        left: 0;
        transform: rotate(0deg);
        transition: .2s ease-in-out;
    }
    span:nth-child(1) {
        top: 0;
    }
    span:nth-child(2),
    span:nth-child(3) {
        top: 50%;
        transform: translateY(-50%);
    }
    span:nth-child(4) {
        bottom: 0;
    }
}

.menu-icon.open {
    span:nth-child(1) {
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        left: 50%;
    }
    span:nth-child(2) {
        transform: rotate(45deg);
    }
    span:nth-child(3) {
        transform: rotate(-45deg);
    }
    span:nth-child(4) {
        top: 18px;
        width: 0;
        left: 50%;
    }
}
`;

const Menu = styled(({ level, items, menu, className = '', open = false, children = null, footnote = null, active = false }) => {
    const openMenuName = menu.openMenuStack[level];
    const menuRef = useRef(null);

    function openSubMenu(e, level, item) {
        if (!getTargetLink(e)) {
            e.stopPropagation();
        }

        if (item.links && item.links.length) {
            menuRef.current.scrollTop = 0;
            menu.toggleSubMenu(level, item.name);
        }
    }

    return (
        <ul ref={menuRef} className={classNames(className, `d-flex flex-column position-absolute d-lg-none pl-5`, {open: open, subMenu: level > 0, active})}>
            {children}
            {
                items.map((item, index) => {
                    const hasSubMenu = item.links && item.links.length;
                    return (
                        <li key={index} onClick={(ev) => openSubMenu(ev, level, item)} className={classNames({'hasSubMenu order-1': hasSubMenu, 'order-2': !hasSubMenu})}>
                            {hasSubMenu ?
                                <span className="text-uppercase bold">{item.name}</span>
                                :
                                <a href={item.path} onClick={menu.toggleMenu} className="text-decoration-none">
                                    <span>{item.name}</span>
                                </a>
                            }
                            {item.links && item.links.length &&
                                <Menu key={item.name} level={level + 1} items={item.links} menu={menu} open={openMenuName == item.name} active={item.name == menu.activeMenu} footnote={footnote || item.footnote}>
                                    <li className="text-redRock text-uppercase bold category-title">
                                        <a href={item.path} onClick={menu.toggleMenu} className="text-decoration-none"><span>{item.name}</span></a>
                                    </li>
                                </Menu>
                            }
                        </li>
                    );
                })
            }
            {footnote ? <li className="footnote order-2">{footnote}</li> : null}
        </ul >
    );
})`
    top: 0;
    left: 0;
    padding-top: 2.778rem;
    background: #FFF;
    width: 100%;
    height: 100%;
    list-style: none;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 9999;
    transition: transform .25s, opacity .25s;
    transform-origin: right center;
    transform: scaleX(0);
    opacity: 0;

    &.open {
        transform: scaleX(1);
        opacity: 1;
    }

    &.subMenu {
        padding-top: 6.056rem;
        padding-left: 1.667rem;
        li {
            padding-left: 1.167rem;
        }
        li.category-title {
            padding-left: 0;
            padding-top: 0;
            font-weight: 500;

            & + li {
                padding-top: 1.778rem;
            }
            span {
                border-bottom: 2px solid var(--redRock);
                padding-bottom: 3px;
            }
        }
    }

    .footnote {
        padding-top: 2.5rem;
        font-size: 0.778rem;
        color: #000;
    }

    li {
        cursor: pointer;
        font-size: 0.889rem;
        font-weight: 400;
        color: #2E2E2B;
        padding-top: 0.833rem;

        &.hasSubMenu {
            padding-top: 1.778rem;
        }

        .bold {
            font-weight: 500;
        }

        a {
            display: block;
            color: inherit;
            width: 100%;
            height: 100%;
       }

       &.order-1 + .order-2 {
        padding-top: 1.278rem;
       }
    }
`;

const Sidebar = styled(({ className = '', open, items, menu, utilityNav, translations }) => {

    function closeSubMenu(e) {
        if (!getTargetLink(e)) {
            e.stopPropagation();
        }
        menu.toggleSubMenu();
    }

    const showSpanish = items.some((item) => {
        return item.showSpanish;
    })

    return (
        <div className={classNames('menu-mobile d-lg-none', {"open": open}, className)}>
            <div className="sidebar-content d-flex flex-column h-100">
                <div className={classNames("d-flex breadcrumb pl-5 align-items-center w-100 position-absolute", {show: menu.openMenuStack.length > 0})}>
                    <span onClick={closeSubMenu}>
                        <FaArrowLeft />
                        <span className="ml-2">{translations.back}</span>
                    </span>
                </div>
                <div className="position-relative flex-grow-1">
                    <Menu items={items} open={true} level={0} menu={menu} active={!menu.activeMenu} />
                </div>
                <div className="utility-nav w-100 pl-5 py-4">
                    <div className="d-flex">
                        {utilityNav?.map((utilityItem, index) => {
                            const { href, label, class: utilityClass } = utilityItem;
                            if (href === '#login') {
                                return null;
                            }
                            return (
                                <NavButton key={index} customClass={classNames("mr-3", utilityClass)} label={label} href={href} variant={NavButtonVariants.BG_BLACK} />
                            );
                        })}
                    </div>
                    {showSpanish &&
                        <div className="spanish-link">
                            <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.LINK, specifier: 'mobile_spanish_link'})} href="/es" className="d-inline-block mr-2 text-decoration-none" onClick={menu.toggleMenu}>
                                <span className="d-inline-block mr-2">Español</span>
                                <FaArrowRight />
                            </a>
                        </div>
                    }

                </div>
            </div>
        </div>
    );
})`
    .breadcrumb {
        padding: 2.778rem 0 0;
        cursor: pointer;
        font-size: 0.778rem;
        font-weight: 700;
        color: #2E2E2B;
        z-index: 10000;
        background: white;

        // Properties for animation
        transition: transform .25s, opacity .25s, visibility .25s;
        transform-origin: right center;
        transform: scaleX(0);
        opacity: 0;
        visibility: hidden;

        &.show {
            transform: scaleY(1);
            opacity: 1;
            visibility: visible;
        }
    }

    .utility-nav {
        background: #FFF;
        .spanish-link {
            padding: 1.5rem 0 0;
            a {
                font-size: 0.778rem;
                color: #000;
                font-weight: 700;
            }
        }
    }
`;
